<template>
  <div class="flex flex-wrap items-center justify-between gap-2">
    <h1>{{ t('PartnerCompany.headlineTop') }}</h1>
    <button class="btn-primary-color-s min-w-28" :disabled="isFormSaving" data-testid="partner-save-button" @click="updatePartnerCompany()">
      <template v-if="!isFormSaving">{{ t('saveButtonLabel') }}</template>
      <template v-else>
        <CircleSpinner size="small" />
      </template>
    </button>
  </div>
  <div class="mt-4 grid grid-cols-8">
    <div class="col-span-8 laptop:col-span-5">
      <p class="text-body-m">
        {{ t('PartnerCompany.companyProfileDescription') }}
      </p>
    </div>
  </div>

  <div class="mt-4 grid grid-cols-8 gap-4">
    <div class="col-span-8 laptop:col-span-5">
      <GeneralInfoForm v-if="partnerCompany" v-model="form.generalInfo" :v$="v$" />

      <CategoriesForm v-if="interests" v-model="form.interests" :v$="v$" :interests="interests" />

      <CompanyGallery v-if="partnerCompany" v-model="form.gallery" :v$="v$" />
    </div>

    <div class="order-first col-span-8 laptop:order-last laptop:col-span-3" data-testid="partner-sidebar">
      <CompanyLogosForm
        v-if="partnerCompany"
        v-model="form.logos"
        :header-image-url="partnerCompany.headerImageUrl"
        :logo-url="partnerCompany.logoUrl"
        :web-logo-url="partnerCompany.webLogoUrl"
        :v$="v$"
      />

      <div class="mt-4 card" data-testid="partner-social-form">
        <h2 class="mb-4 text-headline-m">{{ t('PartnerCompany.socialMediaLinks') }}</h2>
        <div v-for="{ name, iconPath } in socialMediaLinks" :key="name" class="flex items-center">
          <TextInput
            v-model="form.socialMediaLinks[name]"
            :name="name"
            type="url"
            :label="t(`PartnerCompany.${name}`)"
            :append-icon="iconPath"
            :error="getValidationErrors(v$.socialMediaLinks[name])"
          />
        </div>
      </div>
      <SalesContactForm :model-value="salesContactForm" :v$="salesV$" @update:model-value="Object.assign(salesContactForm, $event)" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { mdiYoutube, mdiWeb, mdiInstagram, mdiLinkedin } from '@mdi/js'
import { CircleSpinner, iconTwitterX } from '@ramp106/omrjs-core-ui'
import { TextInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import { ref, computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Contact, PartnerCompany, UpdatePartnerCompanyMutationVariables } from '@/gql/myomr'
import {
  useUpdateContactMutation,
  useCreateContactMutation,
  useInterestsQuery,
  useUpdatePartnerCompanyMutation,
  ContactTypeEnum,
} from '@/gql/myomr'
import { handleMutationResponse } from '@/helpers/handleMutationResponseHelper'
import { showNotification } from '@/helpers/notificationHelper'
import {
  fileMimeTypeRule,
  imageMaxDimensionRule,
  imageMinDimensionRule,
  rasterImageMinDimensionRule,
  rasterImageSquareDimensionRule,
  urlWithSpecificDomainRule,
  validPhoneNumberRule,
  getValidationErrors,
} from '@/helpers/validationHelper'
import { requiredUnless, url, email } from '@/services/validators'

import CompanyGallery from './CompanyGallery.vue'
import CompanyLogosForm from './CompanyLogosForm.vue'

// Config
const HEADER_IMAGE_HEIGHT = 200
const HEADER_IMAGE_WIDTH = 1400
const HEADER_IMAGE_MIME_TYPES = ['image/jpeg', 'image/svg+xml']
const WEB_LOGO_MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml']
const MIN_WEB_LOGO_DIMENSION = 400
const MAX_DESCRIPTION_LENGTH = 750

const socialMediaLinks = [
  { name: 'website', label: 'Website', iconPath: mdiWeb },
  { name: 'linkedinUrl', label: 'LinkedIn', iconPath: mdiLinkedin },
  { name: 'instagram', label: 'Instagram', iconPath: mdiInstagram },
  { name: 'twitter', label: 'X (Twitter)', iconPath: iconTwitterX },
  { name: 'youtube', label: 'Youtube', iconPath: mdiYoutube },
] as const
const { t } = useI18n()

// Props
const props = defineProps<{
  partnerCompany: PartnerCompany
  contactForAppointments: Contact | null | undefined
}>()

// Queries
const { result: interestsResult } = useInterestsQuery()

// Refs
const uploadProgress = ref(0)
const isSubmitting = ref(false)

// Computed properties
const interests = computed(() => interestsResult.value?.interests.nodes)
const companyHasWebLogo = computed<boolean>(() => props.partnerCompany.webLogoUrl != null)

// Mutation definitions
const { mutate: mutatePartnerCompany, loading: mutatePartnerCompanyLoading } = useUpdatePartnerCompanyMutation()
const { mutate: updateContact, loading: updateContactLoading } = useUpdateContactMutation()
const { mutate: mutateNewContact, loading: mutateNewContactLoading } = useCreateContactMutation()
const isFormSaving = computed(
  () => isSubmitting.value || mutatePartnerCompanyLoading.value || updateContactLoading.value || mutateNewContactLoading.value,
)

const images = reactive<{ signedId?: string; file?: File }[]>(props.partnerCompany.images || [])
type CompanyLogosFormData = {
  headerImage: File | null | undefined
  logo: File | undefined
  webLogo: File | undefined
}

// Forms
const form = reactive({
  generalInfo: {
    displayName: props.partnerCompany.displayName ?? '',
    descriptionEn: props.partnerCompany.descriptionEn ?? '',
    descriptionDe: props.partnerCompany.descriptionDe ?? '',
  },
  interests: props.partnerCompany.interests?.map((interest) => interest.id) || [],
  logos: {
    headerImage: undefined,
    logo: undefined,
    webLogo: undefined,
  } as CompanyLogosFormData,
  socialMediaLinks: {
    website: props.partnerCompany.website || '',
    twitter: props.partnerCompany.twitter || '',
    instagram: props.partnerCompany.instagram || '',
    linkedinUrl: props.partnerCompany.linkedinUrl || '',
    youtube: props.partnerCompany.youtube || '',
  },
  gallery: {
    videoUrl: props.partnerCompany.videoUrl || '',
    images: images,
  },
})
const rules = {
  generalInfo: {
    displayName: { required },
    descriptionEn: { required, maxLength: maxLength(MAX_DESCRIPTION_LENGTH) },
    descriptionDe: { required, maxLength: maxLength(MAX_DESCRIPTION_LENGTH) },
  },
  socialMediaLinks: {
    website: { url },
    twitter: { url },
    instagram: { url },
    linkedinUrl: { url },
    youtube: { url },
  },
  interests: [],
  logos: {
    headerImage: {
      fileMimeType: fileMimeTypeRule(t, { mimeTypes: HEADER_IMAGE_MIME_TYPES }),
      imageMinDimension: imageMinDimensionRule(t, { minWidth: HEADER_IMAGE_WIDTH, minHeight: HEADER_IMAGE_HEIGHT }),
      imageMaxDimension: imageMaxDimensionRule(t, { maxWidth: HEADER_IMAGE_WIDTH, maxHeight: HEADER_IMAGE_HEIGHT }),
    },
    logo: {},
    webLogo: {
      requiredIf: requiredUnless(companyHasWebLogo),
      fileMimeType: fileMimeTypeRule(t, { mimeTypes: WEB_LOGO_MIME_TYPES }),
      rasterImageSquare: rasterImageSquareDimensionRule(t),
      rasterImageMinDimension: rasterImageMinDimensionRule(t, {
        minWidth: MIN_WEB_LOGO_DIMENSION,
        minHeight: MIN_WEB_LOGO_DIMENSION,
      }),
    },
  },
  gallery: {
    videoUrl: { urlWithSpecificDomain: urlWithSpecificDomainRule(t, { domainNames: ['youtube.com', 'youtu.be'] }) },
  },
}
const v$ = useVuelidate(rules, form)

const salesContactForm = reactive({
  firstname: props.contactForAppointments?.firstname ?? '',
  name: props.contactForAppointments?.name ?? '',
  title: props.contactForAppointments?.title ?? '',
  email: props.contactForAppointments?.email ?? '',
  phone: props.contactForAppointments?.phone ?? '',
})
const salesContactRules = {
  phone: { validPhoneNumber: validPhoneNumberRule(t) },
  email: { email },
}
const salesV$ = useVuelidate(salesContactRules, salesContactForm)

// Methods
const hasAnyFields = (obj: object): boolean => {
  return Object.values(obj).some((value) => typeof value === 'string' && value.trim() !== '')
}

async function updatePartnerCompany() {
  if (isSubmitting.value) return

  isSubmitting.value = true
  let shouldResetUploads = false

  try {
    const isFormValid = await v$.value.$validate()
    const isSalesFormValid = await salesV$.value.$validate()

    if (!isFormValid || !isSalesFormValid) {
      showNotification(getFormValidationErrorMessage(), 'error')
      scrollToFirstError()
      return
    }

    shouldResetUploads = true
    const mutationData: UpdatePartnerCompanyMutationVariables = {
      ...form.generalInfo,
      ...form.logos,
      ...form.socialMediaLinks,
      videoUrl: form.gallery.videoUrl,
      images: form.gallery.images.map((image) => ('signedId' in image ? { signedId: image.signedId } : { file: image.file })),
      interests: form.interests,
      partnerCompanyId: props.partnerCompany.id,
    }

    if (props.contactForAppointments) {
      await updateContact({ ...salesContactForm, id: props.contactForAppointments.id })
    }

    if (!props.contactForAppointments && hasAnyFields(salesContactForm)) {
      await mutateNewContact({ ...salesContactForm, partnerCompanyId: props.partnerCompany.id, type: ContactTypeEnum.SalesContact })
    }

    const mutationResult = await mutatePartnerCompany(mutationData, {
      context: {
        fetchOptions: {
          useUpload: true,
          onProgress: (progress: ProgressEvent) => {
            const loadedProgress = Math.round((progress.loaded / progress.total) * 100)
            uploadProgress.value = loadedProgress
          },
        },
      },
    })

    window.scrollTo(0, 0)
    handleMutationResponse(t, mutationResult?.data?.updatePartnerCompany, t('PartnerCompany.partnerCompanyUpdated'))
  } catch {
    showNotification(t('EventForm.failedUpdateNotification'), 'error')
  } finally {
    isSubmitting.value = false

    if (shouldResetUploads) {
      uploadProgress.value = 0
      Object.assign(form.logos, {
        headerImage: undefined,
        logo: undefined,
        webLogo: undefined,
      })
    }
  }
}

const scrollToFirstError = () => {
  setTimeout(() => {
    const firstErrorElement = document.querySelector('.text-error')
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 0)
}

const getFormValidationErrorMessage = () =>
  t('BoothExtras.validationError')
    .toString()
    .replace(/<\/?[^>]+>/gi, ' ')
</script>
